/**
 * Place-name localization for Arabic.
 *
 * English place names serve double duty: they are used as DB filter values
 * (compound/area column matches) AND as the human-readable label. This map
 * lets us display the Arabic label while keeping the English value for all
 * query/onClick/param/filter logic.
 *
 * Covers: TOP_LEVEL_DESTINATIONS, REGION_SUBAREAS, SUB_AREA_LAYER2,
 *         compound drill-down lists, and the sub-text tokens shown in the
 *         destination picker.
 */
export const PLACE_AR: Record<string, string> = {
  // ── Top-level regions ──────────────────────────────────────────────────
  "North Coast": "الساحل الشمالي",
  "Red Sea": "البحر الأحمر",
  "Cairo": "القاهرة",
  "Alexandria": "الإسكندرية",
  "El Gouna": "الجونة",

  // ── VESTA (operator 'vesta', 2026-09-03) ───────────────────────────────
  // Cairo compounds are translated where Egyptians actually translate them
  // ("جنوب التسعين", "جنوب الأكاديمية") and transliterated where the compound
  // is a brand ("زايد سيتي", "لا ميرادا"). El Gouna / Soma Bay districts follow
  // the same rule the North Coast entries above use.
  "La Mirada": "لا ميرادا",
  "South Teseen": "جنوب التسعين",
  "South Academy": "جنوب الأكاديمية",
  "Village Gate, Palm Hills": "فيلدج جيت، بالم هيلز",
  "Zayed City": "زايد سيتي",
  "Zayed Dunes": "زايد ديونز",
  "Lake View Residence": "ليك فيو ريزيدنس",
  "North Golf": "نورث جولف",
  "New Marina": "المارينا الجديدة",
  "Shedwan": "شدوان",
  "Soma Bay": "سوما باي",
  "Baywest, Soma Bay": "باي ويست، سوما باي",
  "Safaga": "سفاجا",

  // ── North Coast compounds ──────────────────────────────────────────────
  "Marassi": "مراسي",
  "Marassi Bay": "مراسي باي",
  "Fouka Bay": "فوكا باي",
  "Gaia": "جايا",
  "Playa": "بلايا",
  "D-Bay": "دي باي",
  "Hacienda West": "هاسيندا ويست",

  // Alamein (added 2026-08-14). "City Edge" is the developer name on the
  // signage, so it is transliterated rather than translated. "Blanche Hotel"
  // is BirdNest's pre-rename label for Marsa Boutique Hotel — kept so a row
  // that misses the rename still localizes to the right place.
  "City Edge": "سيتي إيدج",
  "Marsa Boutique Hotel": "مرسى بوتيك هوتيل",
  "Blanche Hotel": "مرسى بوتيك هوتيل",
  "New Alamein": "العلمين الجديدة",

  // Almaza Bay (operator 'almaza', Marsa Matrouh — far-west North Coast).
  // "Almaza Bay" is the compound + the North-Coast sub-area chip; Beachtown /
  // Residences / Bay Homes / Selection are the `units.area` sub-communities;
  // "Marsa Matrouh" is the display city.
  "Almaza Bay": "ألماظة باي",
  "Beachtown": "بيتش تاون",
  "Residences": "ريزيدنسز",
  "Bay Homes": "باي هومز",
  "Selection": "سيليكشن",
  "Marsa Matrouh": "مرسى مطروح",

  // Marassi sub-zones (stored as compound column values in DB)
  "Marina 1": "مارينا 1",
  "Marina 2": "مارينا 2",
  "Marina Views": "مارينا فيوز",
  "Marina views": "مارينا فيوز",
  "Greek": "جريك",
  "Greek Village": "القرية اليونانية",
  "Lea": "ليا",
  "Faya": "فايا",
  "Blanca": "بلانكا",
  "Verdi": "فيردي",
  "Skaia": "سكايا",
  "Catania": "كاتانيا",
  "Riva": "ريفا",
  "Vento": "فينتو",

  // ── Red Sea compounds ──────────────────────────────────────────────────
  "Ain Sokhna": "العين السخنة",

  // ── Cairo sub-areas ────────────────────────────────────────────────────
  "Sheikh Zayed": "الشيخ زايد",
  "Sheikh Zayed City": "مدينة الشيخ زايد",
  "New Cairo": "القاهرة الجديدة",
  "Zamalek": "الزمالك",
  "Downtown": "وسط البلد",
  "Downtown Cairo": "وسط القاهرة",
  "Giza": "الجيزة",
  "Maadi": "المعادي",
  "Heliopolis": "مصر الجديدة",
  "6th of October": "السادس من أكتوبر",
  "Pyramids District": "منطقة الأهرام",

  // BirdNest Cairo compounds
  "Aura Cairo Festival City": "أورا القاهرة فستيفال سيتي",
  "Sodic Villette New Cairo": "سوديك فيليت القاهرة الجديدة",
  "BIRD NEST at Cairo Festival City": "بيرد نيست في القاهرة فستيفال سيتي",
  "Villette Residence": "فيليت ريزيدنس",
  "Boutique Nest": "بوتيك نيست",
  "BIRD NEST Near Arkan Plaza": "بيرد نيست بجوار أركان بلازا",
  "ZED Towers – Sheikh Zayed": "زيد تاورز — الشيخ زايد",
  // Community-page display names (2026-10-03). Not stored `units` values, so
  // they change no listing card or filter label.
  "ZED Towers": "زيد تاورز",
  "Mivida": "ميفيدا",
  "Aeon Towers": "أيون تاورز",
  // Sub-places named in listing <title>s (src/lib/listing-seo-title.ts).
  "Vida Marassi": "فيدا مراسي",
  "Silver Palm": "سيلفر بالم",
  "Garden City": "جاردن سيتي",
  "Golf Estate": "جولف إستيت",
  "Al Jazi Developments": "الجازي للتطوير",
  "BIRD NEST Karma Compound": "بيرد نيست كارما كمباوند",

  // Layer-2 sub-areas (Cairo / New Cairo)
  "Al Jazi": "الجازي",
  "Sodic Villette": "سوديك فيليت",
  "Cairo Festival City": "القاهرة فستيفال سيتي",

  // ── Alexandria sub-areas ───────────────────────────────────────────────
  "Corniche Apartments": "شقق الكورنيش",
  "Little Venice": "ليتل فينيس",
  "Naby Danial": "نبي دانيال",
  "Corniche": "الكورنيش",

  // ── El Gouna compounds ─────────────────────────────────────────────────
  "El Gouna Village": "قرية الجونة",
  "Abu Tig Marina": "مارينا أبو تيج",
  "Kafr El Gouna": "كفر الجونة",

  // ── Additional compounds / buildings seen in unit `compound` data ──────
  "Arkan Plaza": "أركان بلازا",
  "Om Kolthoom Hotel": "فندق أم كلثوم",
  "Bidair Pasha House": "بيت بدير باشا",
  "HANNAUX": "هانو",
  "Kasr Nile": "قصر النيل",
  "Longchamps hotel": "فندق لونشان",
  "M Residence": "إم ريزيدنس",
  "Saint J Hotel": "فندق سانت جيه",
  "Adly apartments": "شقق عدلي",
  "Maspero Apartments": "شقق ماسبيرو",
  "Marassi Marina Views": "مراسي مارينا فيوز",
  // Zamalek building tokens (appear inside "Zamalek · X" compound strings,
  // localized per-token by localizePlaceList)
  "Jeddah Tower": "جدة تاور",
  "Jeddah Tower, Zamalek": "جدة تاور، الزمالك",
  "Nile View": "إطلالة على النيل",
  "Om Kolthoom": "أم كلثوم",
  "Nagui": "ناجي",
  "Chroma": "كروما",

  // ── El Gouna + Xuru Cairo compounds ───────────────────────────────────
  // Transliterated, matching how North Coast compounds are handled (Marassi →
  // مراسي) — these are brand names, not descriptive places. Cards render the
  // COMPOUND string, so a missing label shows English on /ar.
  "Nasr City": "مدينة نصر",
  "Sheraton Heliopolis": "شيراتون مصر الجديدة",
  "Valory Sheraton": "فالوري شيراتون",
  "Mangroovy": "منجروفي",
  "Mangroovy Residence": "منجروفي ريزيدنس",
  "Sabina": "سابينا",
  "Maraya": "مرايا",
  "Fanadir": "فنادير",
  "Fanadir Bay": "فنادير باي",
  "Cyan Residence": "سيان ريزيدنس",
  "G-Cribs": "جي كريبس",
  "Ancient Hills": "أنشنت هيلز",
  "Ancient Sands": "أنشنت ساندز",
  "Tawila": "طويلة",
  "Joubal": "جوبال",

  // ── Sub-text tokens (picker sub-lines) ────────────────────────────────
  "Hurghada Red Sea": "الغردقة",
  "coming soon": "قريبًا",
};

/**
 * Return the Arabic display label for a place name if locale is "ar",
 * falling back to the original English name for unknown entries.
 */
// Case-insensitive fallback so DB casing variants ("AEON TOWERS", "Marina
// views") still resolve to the same Arabic label.
const PLACE_AR_LC: Record<string, string> = Object.fromEntries(
  Object.entries(PLACE_AR).map(([k, v]) => [k.toLowerCase(), v]),
);

// Canonical English display name keyed by lowercased name. Area values are
// round-tripped through the URL as lowercase slugs ("marassi", "new cairo"),
// so when the search UI seeds a single selected area from the URL the raw
// value must still render with proper casing ("Marassi", "New Cairo") in the
// English UI — not the leaked slug. Unknown places fall through unchanged.
const PLACE_CANONICAL_LC: Record<string, string> = Object.fromEntries(
  Object.keys(PLACE_AR).map((k) => [k.toLowerCase(), k]),
);

export function placeLabel(name: string, locale: string): string {
  const key = name.trim();
  const canonical = PLACE_CANONICAL_LC[key.toLowerCase()] ?? key;
  if (locale !== "ar") return canonical;
  return PLACE_AR[canonical] ?? PLACE_AR_LC[canonical.toLowerCase()] ?? canonical;
}

/**
 * Localize a separator-joined place list like "Marassi · Fouka Bay · Gaia".
 * Each token is run through placeLabel individually.
 */
export function localizePlaceList(
  text: string,
  locale: string,
  sep = " · ",
): string {
  if (locale !== "ar" || !text) return text;
  return text
    .split(sep)
    .map((t) => placeLabel(t.trim(), locale))
    .join(sep);
}
